import { useMemo, useState, type ReactNode } from "react";
import { computeQuote, type ContentSelection } from "../../../convex/lib/quote";
import {
  ALL_CONTENT,
  formatCount,
  messagesToSliderPos,
  MSG_SLIDER,
  sliderPosToMessages,
  syntheticScan,
} from "../../lib/estimator";
import { ContentToggles } from "./controls";
import { QuoteReceipt } from "./QuoteReceipt";

/** Pre-connect estimator: sliders → live quote, no account needed. */
export function MarketingEstimator({ onGetStarted }: { onGetStarted: () => void }) {
  // The message slider runs on a log scale (position 0…steps → 1k…250k), so the
  // floor ($100 / 1,000 messages) is selectable and 1k–50k has real granularity.
  const [msgPos, setMsgPos] = useState(() => messagesToSliderPos(50_000));
  const [picsGB, setPicsGB] = useState(12);
  const [videoGB, setVideoGB] = useState(6);
  const [filesGB, setFilesGB] = useState(1);
  const [content, setContent] = useState<ContentSelection>({ ...ALL_CONTENT });

  const messages = sliderPosToMessages(msgPos);

  const quote = useMemo(
    () =>
      computeQuote(
        {
          channelIds: ["synthetic"],
          contentTypes: content,
          timeRange: { kind: "all" },
        },
        syntheticScan({ messages, picsGB, videoGB, filesGB }),
      ),
    [messages, picsGB, videoGB, filesGB, content],
  );

  return (
    <div className="est card">
      <div className="est-controls">
        <div className="est-controls-head">
          <span className="eyebrow">Estimate</span>
          <p className="muted est-sub">
            Rough sizes are fine — connect later for an exact, per-channel quote.
          </p>
        </div>

        <ContentToggles value={content} onChange={setContent} />

        <div className="est-sliders">
          {content.text && (
            <Slider
              id="est-msgs"
              label="Messages"
              value={<b>{formatCount(messages)}</b>}
              min={0}
              max={MSG_SLIDER.steps}
              step={1}
              raw={msgPos}
              onChange={setMsgPos}
            />
          )}
          {content.pics && (
            <Slider id="est-pics" label="Pictures" value={<b>{picsGB} GB</b>} min={0} max={200} step={1} raw={picsGB} onChange={setPicsGB} />
          )}
          {content.video && (
            <Slider id="est-video" label="Video" value={<b>{videoGB} GB</b>} min={0} max={200} step={1} raw={videoGB} onChange={setVideoGB} />
          )}
          {content.files && (
            <Slider id="est-files" label="Files" value={<b>{filesGB} GB</b>} min={0} max={100} step={1} raw={filesGB} onChange={setFilesGB} />
          )}
        </div>

        <div className="est-included">
          <span className="eyebrow">Team &amp; channels · always free</span>
          <IncludedSlider id="est-users" label="Users" max={500} initial={25} />
          <IncludedSlider id="est-channels" label="Channels" max={250} initial={12} />
          <p className="included-note">
            Max out users and channels — you’re never charged for them. You only
            pay for the data you bring over.
          </p>
        </div>
      </div>

      <QuoteReceipt quote={quote}>
        <button className="btn btn-primary" onClick={onGetStarted}>
          Start your migration
        </button>
        <p className="hint-line mono">
          Media uploads to Discord natively (10 MB free / 50–100 MB on a boosted
          server). We flag oversized files after you connect.
        </p>
      </QuoteReceipt>
    </div>
  );
}

function Slider({
  id,
  label,
  value,
  min,
  max,
  step,
  raw,
  onChange,
}: {
  id: string;
  label: string;
  value: ReactNode;
  min: number;
  max: number;
  step: number;
  raw: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>
        {label} {value}
      </label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={raw}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

/**
 * A slider whose value NEVER changes the price — it exists to make the "$0,
 * unlimited, included" promise tangible for users and channels.
 */
function IncludedSlider({
  id,
  label,
  max,
  initial,
}: {
  id: string;
  label: string;
  max: number;
  initial: number;
}) {
  const [v, setV] = useState(initial);
  const atMax = v >= max;
  return (
    <div className="field">
      <label htmlFor={id}>
        <span>
          {label}{" "}
          <b>{atMax ? "Unlimited" : v.toLocaleString("en-US")}</b>
        </span>
        <span className="incl-badge mono">$0 · included</span>
      </label>
      <input
        id={id}
        type="range"
        className="range-included"
        min={0}
        max={max}
        step={1}
        value={v}
        onChange={(e) => setV(Number(e.target.value))}
      />
    </div>
  );
}
